import { QueryClient } from "@tanstack/react-query"
import { isAppError } from "./problem"

/**
 * Per-domain staleTime overrides live in each slice's api/ wrapper (PDD §9.6).
 * These are the floors.
 */
export const STALE = {
  /** `getMe`: invalidated explicitly, never by age (LLD §5.7). */
  session: Infinity,
  /** Permission catalogue, settings definitions, enums. */
  static: 60 * 60_000,
  /** `getConfig`, one rule everywhere (LLD §5.7): 5 minutes, refetched on focus, no interval. */
  reference: 5 * 60_000,
  /** Devices, API keys, webhooks, plans. */
  content: 5 * 60_000,
  /** Users, roles, messages. */
  directory: 2 * 60_000,
  /** Wallet, top-ups, payments -- money moves. */
  operational: 30_000,
} as const

export const POLL = {
  alerts: 60_000,
  /** A top-up still pending (LLD §9.9, FR-DSH-BIL-03). */
  topup: 2_000,
  /** A message not yet delivered or failed (LLD §9.6). */
  message: 5_000,
  /** A batch still pending or processing. */
  batch: 3_000,
  jobs: 10_000,
  /** The system's status page (LLD §9.24). */
  status: 30_000,
  exports: 5_000,
} as const

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE.operational,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: (failureCount, error) => {
          if (!isAppError(error)) return failureCount < 1
          if (error.kind === "network") return failureCount < 2
          // Never retry a 4xx: the answer will not change, and on a slow
          // connection a retry storm is exactly the wrong behaviour.
          if (error.status >= 500) return failureCount < 2
          return false
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        /**
         * Mutations NEVER auto-retry. Idempotency keys make a retry harmless,
         * but a silent retry hides a failure the operator needs to see -- and
         * an automatic retry storm on a Cameroonian mobile connection is the
         * wrong behaviour twice over. Retry is an action the user takes, from
         * an error surface that explains what happened (LLD §5.5).
         */
        retry: false,
      },
    },
  })
}

export const queryClient = createQueryClient()
